import { createSelector } from 'reselect';
import equals from 'lodash/fp/equals';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';
import isFunction from 'lodash/isFunction';
import { FIELD_TYPES } from '@atlassian/jira-polaris-domain-field/src/field-types/index.tsx';
import type { Field, FieldKey } from '@atlassian/jira-polaris-domain-field/src/field/types.tsx';
import type { LocalIssueId } from '@atlassian/jira-polaris-domain-idea/src/idea/types.tsx';
import type { ConnectionFieldValue } from '@atlassian/jira-polaris-domain-field/src/field-types/connection/types.tsx';
import {
	CONNECTION_FIELD_FILTER,
	CONNECTION_FIELD_FILTER_BOARD_COLUMN,
	CONNECTION_FIELD_FILTER_VIEW_GROUP,
	GENERIC_FIELD_FILTER,
	NUMERIC_FIELD_FILTER,
	type ConnectionFieldFilter,
	type FieldValueFilter,
	type Filter,
	type NumericFieldFilter,
} from '@atlassian/jira-polaris-domain-view/src/filter/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { INTERVAL_FIELD_SOURCES } from '@atlassian/jira-polaris-domain-field/src/field/interval/index.tsx';
import type { Props, State } from '../types.tsx';
import { isMatchingConnectionFieldFilter } from '../utils/connection-field-filters.tsx';
import { fieldMapping } from '../utils/field-mapping/index.tsx';
import type { FieldMapping } from '../utils/field-mapping/types.tsx';
import { createGetField, getAllFieldsByKey, getFieldMappings, getFields } from './fields.tsx';
import { createGetFilterTypeForField, getIssueIdsConsideringArchived } from './filters.tsx';
import {
	getJiraIdToLocalIssueId,
	getIssuesWithBasicPropertiesMap,
	getConnectionIssueIds,
	getConnectedIssuesWithBasicPropertiesMap,
} from './issue-ids.tsx';
import {
	createGetIssueType,
	createGetSummary,
	getConnectionProperties,
	getSelectedIssueLocalIssueId,
	createGetProjectIdSelector,
} from './properties/index.tsx';
import {
	createCombinedComparator,
	createIdeaComparatorForFieldComparator,
	type IdeaComparator,
} from './sort.tsx';

export const createGetAllIssueIdsMatchingConnectionFieldFilters = (
	localIssueId: LocalIssueId,
	fieldKey: FieldKey,
) =>
	createSelector(
		getIssueIdsConsideringArchived,
		createGetField(fieldKey),
		(state, props) => ({ state, props }),
		(issueIds, connectionField, { state, props }) => {
			const projectIssuesIds = issueIds.filter((issueId) => {
				const issueType = createGetIssueType(issueId)(state, props);
				const projectId = createGetProjectIdSelector(issueId)(state);

				return (
					issueId !== localIssueId &&
					isMatchingConnectionFieldFilter({
						filters: connectionField?.configuration?.issueTypeFilters || [],
						issueTypeId: issueType?.id,
						issueTypeName: issueType?.name,
						projectId,
					})
				);
			});
			return projectIssuesIds;
		},
	);

/*
 * Used to check if a connection field can be used for a specific issue
 * does not take into account project id
 */
export const createIsIssueMatchingConnectionFieldFilter = (
	localIssueId: LocalIssueId = '',
	fieldKey: FieldKey,
) =>
	createSelector(
		createGetField(fieldKey),
		(state: State, props?: Props) => ({ state, props }),
		(connectionField, { state, props }) => {
			const issueType = createGetIssueType(localIssueId)(state, props);

			return isMatchingConnectionFieldFilter({
				filters: connectionField?.configuration?.issueTypeFilters || [],
				issueTypeId: issueType?.id,
				issueTypeName: issueType?.name,
			});
		},
	);

const filterArchivedConnectionFieldIssueIds = (
	connections: ConnectionFieldValue[] = [],
	issuesWithBasicPropertiesMap: ReturnType<typeof getIssuesWithBasicPropertiesMap>,
	connectedIssuesWithBasicPropertiesMap: ReturnType<
		typeof getConnectedIssuesWithBasicPropertiesMap
	>,
	connectionIssueIds: ReturnType<typeof getConnectionIssueIds>,
	jiraIdToLocalIssueId: Record<number, string>,
) =>
	connections.filter(({ id }) => {
		const localId = jiraIdToLocalIssueId[parseInt(id, 10)];

		if (fg('jpd_connections-count-on-full-issue-view')) {
			if (!localId) {
				return true;
			}
		}

		if (fg('jpd_open-issues-from-diff-container-in-sidebar')) {
			if (issuesWithBasicPropertiesMap[localId]) {
				return !issuesWithBasicPropertiesMap[localId]?.isArchived;
			}

			if (connectedIssuesWithBasicPropertiesMap[localId]) {
				return !connectedIssuesWithBasicPropertiesMap[localId]?.isArchived;
			}

			return false;
		}

		return (
			(issuesWithBasicPropertiesMap[localId] &&
				!issuesWithBasicPropertiesMap[localId]?.isArchived) ||
			connectionIssueIds.includes(localId)
		);
	});

const EMPTY_CONNECTIONS: ConnectionFieldValue[] = [];

export const createGetConnectionFieldIssueIds = (fieldKey: FieldKey, localIssueId?: LocalIssueId) =>
	createSelector(
		getConnectionProperties,
		getIssuesWithBasicPropertiesMap,
		getConnectedIssuesWithBasicPropertiesMap,
		getConnectionIssueIds,
		getJiraIdToLocalIssueId,
		(
			connectionProperties,
			issuesWithBasicPropertiesMap,
			connectedIssuesWithBasicPropertiesMap,
			connectionIssueIds,
			jiraIdToLocalIssueId,
		) => {
			if (!localIssueId) {
				return EMPTY_CONNECTIONS;
			}

			const result = filterArchivedConnectionFieldIssueIds(
				connectionProperties[fieldKey]?.[localIssueId],
				issuesWithBasicPropertiesMap,
				connectedIssuesWithBasicPropertiesMap,
				connectionIssueIds,
				jiraIdToLocalIssueId,
			);
			const sortedResult = [...result].sort(
				({ id: id1 }, { id: id2 }) => Number(id1) - Number(id2),
			);
			return sortedResult;
		},
	);

type ConnectionFieldOption = {
	fieldKey: string;
	fieldValue: unknown;
};

type ConnectionFieldOptions = {
	column?: ConnectionFieldOption;
	group?: ConnectionFieldOption;
};

const getFilters = ({ containerProps }: State) => containerProps?.filter;

export const isObjectWithId = (value: unknown): value is { id: string } =>
	typeof value === 'object' && value !== null && 'id' in value && typeof value.id === 'string';

export const getFilterByFieldValueAndFilterType = (
	{ fieldKey, fieldValue }: ConnectionFieldOption,
	filterType: Filter['type'] | undefined,
): FieldValueFilter | NumericFieldFilter | undefined => {
	if (typeof fieldValue === 'number' && filterType === NUMERIC_FIELD_FILTER) {
		return {
			type: NUMERIC_FIELD_FILTER,
			field: fieldKey,
			values: [{ operator: 'EQ', numericValue: fieldValue }],
		};
	}

	if (filterType === GENERIC_FIELD_FILTER) {
		if (Array.isArray(fieldValue)) {
			return {
				type: GENERIC_FIELD_FILTER,
				field: fieldKey,
				values: fieldValue.filter(isObjectWithId).map(({ id }) => ({ stringValue: id })),
			};
		}

		if (isObjectWithId(fieldValue)) {
			return {
				type: GENERIC_FIELD_FILTER,
				field: fieldKey,
				values: [{ stringValue: fieldValue.id }],
			};
		}
	}

	return undefined;
};

export const createGetConnectionFieldIssueIdsWithOptions = (
	fieldKey: FieldKey,
	localIssueId: LocalIssueId,
	{ column, group }: ConnectionFieldOptions,
) => {
	const getConnectionFieldIssueIds = createGetConnectionFieldIssueIds(fieldKey, localIssueId);

	const getMappingColumn = createSelector(
		({ containerProps }) => containerProps?.issuesRemote,
		getFields,
		getAllFieldsByKey,
		(issuesRemote, fields, fieldsByKey) =>
			column && issuesRemote && fieldMapping(issuesRemote, fields, fieldsByKey[column.fieldKey]),
	);

	const getMappingGroup = createSelector(
		({ containerProps }) => containerProps?.issuesRemote,
		getFields,
		getAllFieldsByKey,
		(issuesRemote, fields, fieldsByKey) =>
			group && issuesRemote && fieldMapping(issuesRemote, fields, fieldsByKey[group.fieldKey]),
	);

	const getConnectionFieldFilters = createSelector(getFilters, (filters) => {
		return (
			filters?.filter((filter): filter is ConnectionFieldFilter => {
				return filter.type === CONNECTION_FIELD_FILTER && filter.field === fieldKey;
			}) ?? []
		);
	});

	const getShouldMatchColumn = createSelector(getConnectionFieldFilters, (connectionFieldFilters) =>
		connectionFieldFilters.some((filter) =>
			filter.values.some((value) => value.enumValue === CONNECTION_FIELD_FILTER_BOARD_COLUMN),
		),
	);

	const getShouldMatchGroup = createSelector(getConnectionFieldFilters, (connectionFieldFilters) =>
		connectionFieldFilters.some((filter) =>
			filter.values.some((value) => value.enumValue === CONNECTION_FIELD_FILTER_VIEW_GROUP),
		),
	);

	// For memoized result
	let lastResult: ConnectionFieldValue[] = [];

	return createSelector(
		getConnectionFieldIssueIds,
		getJiraIdToLocalIssueId,
		getMappingColumn,
		getMappingGroup,
		getShouldMatchColumn,
		getShouldMatchGroup,
		(state, props) => ({ state, props }),
		(
			issueIds,
			jiraIdToLocalIssueId,
			mappingColumn,
			mappingGroup,
			shouldMatchColumn,
			shouldMatchGroup,
			{ state, props },
		) => {
			const columnMatching = (v: ConnectionFieldValue) => {
				if (shouldMatchColumn === false || mappingColumn === undefined || column === undefined) {
					return true;
				}

				const localId = jiraIdToLocalIssueId[parseInt(v.id, 10)];
				const fieldValue = mappingColumn.valueAccessor(state, props, localId);
				return mappingColumn.comparator(column.fieldValue, fieldValue, 'ASC') === 0;
			};

			const groupMatching = (v: ConnectionFieldValue) => {
				if (shouldMatchGroup === false || mappingGroup === undefined || group === undefined) {
					return true;
				}

				const localId = jiraIdToLocalIssueId[parseInt(v.id, 10)];
				const fieldValue = mappingGroup.valueAccessor(state, props, localId);
				return mappingGroup.comparator(group.fieldValue, fieldValue, 'ASC') === 0;
			};

			const columnFilterType = column?.fieldKey
				? createGetFilterTypeForField(column?.fieldKey)(state, props)
				: undefined;

			const groupFilterType = group?.fieldKey
				? createGetFilterTypeForField(group?.fieldKey)(state, props)
				: undefined;

			const isMatching = (
				issueId: IssueId,
				option: ConnectionFieldOption | undefined,
				shouldMatch: boolean,
				mapping: FieldMapping<unknown> | undefined,
				filterType: Filter['type'] | undefined,
			): boolean => {
				if (
					shouldMatch === false ||
					mapping === undefined ||
					option === undefined ||
					filterType === undefined
				) {
					return true;
				}

				const filter = getFilterByFieldValueAndFilterType(option, filterType);
				const filterFunction = filter ? mapping.getFilter(filter) : undefined;

				if (filterFunction === undefined) {
					return false;
				}

				const localId = jiraIdToLocalIssueId[parseInt(issueId, 10)];
				const fieldValue = mapping.valueAccessor(state, props, localId);

				if (isFunction(filterFunction)) {
					return filterFunction(fieldValue, state, props, localId);
				}

				return filterFunction.filterFunction(fieldValue, state, props, localId);
			};

			const result = fg('jpd_fix_filter_in_connection_field')
				? issueIds.filter(
						(v) =>
							isMatching(v.id, column, shouldMatchColumn, mappingColumn, columnFilterType) &&
							isMatching(v.id, group, shouldMatchGroup, mappingGroup, groupFilterType),
					)
				: issueIds.filter((v) => columnMatching(v) && groupMatching(v));

			if (equals(result, lastResult)) {
				// So that we don't send new array to React. This should prevent extra renders.
				return lastResult;
			}
			// Update the memoized value
			lastResult = result;
			return result;
		},
	);
};

const createGetSelectedIssueConnectionFieldIssueIds = (connectionFieldKey: FieldKey) =>
	createSelector(
		getSelectedIssueLocalIssueId,
		getConnectionProperties,
		getIssuesWithBasicPropertiesMap,
		getConnectedIssuesWithBasicPropertiesMap,
		getConnectionIssueIds,
		getJiraIdToLocalIssueId,
		(
			localIssueId,
			connectionProperties,
			issuesWithBasicPropertiesMap,
			connectedIssuesWithBasicPropertiesMap,
			connectionIssueIds,
			jiraIdToLocalIssueId,
		) => {
			if (!localIssueId) {
				return EMPTY_CONNECTIONS;
			}

			const result = filterArchivedConnectionFieldIssueIds(
				connectionProperties[connectionFieldKey]?.[localIssueId],
				issuesWithBasicPropertiesMap,
				connectedIssuesWithBasicPropertiesMap,
				connectionIssueIds,
				jiraIdToLocalIssueId,
			);
			const sortedResult = [...result].sort(
				({ id: id1 }, { id: id2 }) => Number(id1) - Number(id2),
			);
			return sortedResult;
		},
	);

export const createGetColumnAndGroupAwareConnectionFieldIssueIdsSorted = (
	connectionFieldKey: FieldKey,
	localIssueId: LocalIssueId,
	options: ConnectionFieldOptions,
) =>
	createSelector(
		createGetConnectionFieldIssueIdsWithOptions(connectionFieldKey, localIssueId, options),
		createGetField(connectionFieldKey),
		getAllFieldsByKey,
		getFields,
		getJiraIdToLocalIssueId,
		({ containerProps }) => containerProps?.issuesRemote,
		(state, props) => ({ state, props }),
		(
			ids,
			connectionField,
			fieldsByKey,
			fields,
			jiraIdToLocalIssueId,
			issuesRemote,
			{ state, props },
		) => {
			if (!connectionField || !issuesRemote) {
				return [];
			}

			const sortBy = connectionField.configuration?.issueViewLayout?.sort || [];

			const configuredComparators = sortBy.reduce<Array<IdeaComparator>>((comparators, sort) => {
				const mapping = fieldMapping(issuesRemote, fields, fieldsByKey[sort.fieldKey]);

				const comparator = createIdeaComparatorForFieldComparator(
					state,
					props,
					mapping.valueAccessor,
					mapping.comparator,
					mapping.comparatorWithMapping,
					sort.order === 'ASC',
				);

				comparators.push(comparator);

				return comparators;
			}, []);

			const combinedComparator = createCombinedComparator(configuredComparators);

			const idsCopy = [...ids];

			idsCopy.sort((a, b) =>
				combinedComparator(
					jiraIdToLocalIssueId[parseInt(a.id, 10)],
					jiraIdToLocalIssueId[parseInt(b.id, 10)],
				),
			);

			return idsCopy;
		},
	);

type ConnectionsMap = Map<
	LocalIssueId,
	Array<{
		field: Field;
		key: FieldKey;
		value: {
			connections: Array<{ summary: string; highlightedFieldValue: unknown }>;
			highlightedField?: Field;
		};
	}>
>;

type ColumnAndGroupIssue = {
	id: string;
	localIssueId: LocalIssueId;
	column: {
		fieldKey: FieldKey;
		fieldValue: unknown;
	};
	group?: {
		fieldKey: FieldKey;
		fieldValue: unknown;
	};
};

const EMPTY_CONNECTIONS_MAP: ConnectionsMap = new Map();

export const createGetColumnAndGroupAwareConnections = (
	issues: ColumnAndGroupIssue[],
	connectionFieldKeys: FieldKey[],
) => {
	let lastResult: ConnectionsMap;

	return createSelector(
		getAllFieldsByKey,
		getFields,
		getJiraIdToLocalIssueId,
		({ containerProps }) => containerProps?.issuesRemote,
		(state, props) => ({ state, props }),
		(fieldsByKey, fields, jiraIdToLocalIssueId, issueRemote, { state, props }) => {
			if (!issueRemote) {
				return EMPTY_CONNECTIONS_MAP;
			}

			const connectionsMap: ConnectionsMap = new Map();

			issues.forEach(({ id, localIssueId, group, column }) => {
				if (!connectionsMap.has(id)) {
					connectionsMap.set(id, []);
				}

				const issueConnectionFields = connectionsMap.get(id);

				connectionFieldKeys.forEach((fieldKey) => {
					const field = fieldsByKey[fieldKey];

					if (!field) {
						return;
					}

					if (fg('jpd-hide-connection-field-of-same-type-on-card')) {
						const isMatchingFieldFilter = createIsIssueMatchingConnectionFieldFilter(
							localIssueId,
							fieldKey,
						)(state, props);

						if (isMatchingFieldFilter) {
							return;
						}
					}

					const filteredConnections = createGetConnectionFieldIssueIdsWithOptions(
						field.key,
						localIssueId,
						{ column, group },
					)(state, props);
					const highlightedField = field.configuration?.issueViewLayout?.fields[0];
					const highlightedFieldMapping = highlightedField
						? fieldMapping(issueRemote, fields, fieldsByKey[highlightedField])
						: undefined;
					const connections = filteredConnections.map((connection) => {
						const connectionLocalIssueId = jiraIdToLocalIssueId[parseInt(connection.id, 10)];

						return {
							summary: createGetSummary(connectionLocalIssueId)(state, props),
							highlightedFieldValue: highlightedFieldMapping?.valueAccessor(
								state,
								props,
								connectionLocalIssueId,
							),
						};
					});

					if (!connections.length) {
						return;
					}

					issueConnectionFields?.push({
						field,
						key: field.key,
						value: {
							highlightedField: highlightedField ? fieldsByKey[highlightedField] : undefined,
							connections,
						},
					});
				});
			});

			if (isEqual(lastResult, connectionsMap)) {
				return lastResult;
			}

			lastResult = connectionsMap;
			return connectionsMap;
		},
	);
};

export const createGetConnectionFieldSortedIssueIds = (
	connectionFieldKey: FieldKey,
	localIssueId?: LocalIssueId,
) =>
	createSelector(
		createGetConnectionFieldIssueIds(connectionFieldKey, localIssueId),
		createGetField(connectionFieldKey),
		getAllFieldsByKey,
		getFields,
		getJiraIdToLocalIssueId,
		({ containerProps }) => containerProps?.issuesRemote,
		(state, props) => ({ state, props }),
		(
			ids,
			connectionField,
			fieldsByKey,
			fields,
			jiraIdToLocalIssueId,
			issuesRemote,
			{ state, props },
		) => {
			if (!connectionField || !issuesRemote) {
				return [];
			}

			const sortBy = connectionField.configuration?.issueViewLayout?.sort || [];

			const configuredComparators = sortBy.reduce<Array<IdeaComparator>>((comparators, sort) => {
				const mapping = fieldMapping(issuesRemote, fields, fieldsByKey[sort.fieldKey]);

				const comparator = createIdeaComparatorForFieldComparator(
					state,
					props,
					mapping.valueAccessor,
					mapping.comparator,
					mapping.comparatorWithMapping,
					sort.order === 'ASC',
				);

				comparators.push(comparator);

				return comparators;
			}, []);

			const combinedComparator = createCombinedComparator(configuredComparators);

			const idsCopy = [...ids];

			idsCopy.sort((a, b) =>
				combinedComparator(
					jiraIdToLocalIssueId[parseInt(a.id, 10)],
					jiraIdToLocalIssueId[parseInt(b.id, 10)],
				),
			);

			return idsCopy;
		},
	);

export const createGetSelectedIssuesConnectionFieldIssuesCount = (fieldKey: FieldKey) =>
	createSelector(createGetSelectedIssueConnectionFieldIssueIds(fieldKey), (ids) => ids.length);

export const getSelectedIssueConfiguredConnectionFields = createSelector(
	getSelectedIssueLocalIssueId,
	getFields,
	(state, props) => ({ state, props }),
	(localIssueId, fields, { state, props }) => {
		const issueType = createGetIssueType(localIssueId)(state, props);

		return fields.reduce<Field[]>((res, field) => {
			const isFieldAssociatedToIssue =
				issueType?.id &&
				(field.issueTypes?.includes(issueType.id) ||
					!!props?.connectionsFieldsAssociations?.[field.key]?.includes(issueType.id));

			if (
				issueType &&
				field.type === FIELD_TYPES.CONNECTION &&
				field.configuration?.issueTypeFilters &&
				field.configuration.issueTypeFilters.length > 0 &&
				!isMatchingConnectionFieldFilter({
					filters: field.configuration.issueTypeFilters,
					issueTypeId: issueType?.id,
					issueTypeName: issueType?.name,
				}) &&
				isFieldAssociatedToIssue
			) {
				res.push(field);
			}
			return res;
		}, []);
	},
);

export const getSelectedIssueConfiguredConnectionFieldsCount = createSelector(
	getSelectedIssueConfiguredConnectionFields,
	(fields) => fields.length,
);

export const createGetSelectedIssueConnectionsCount = (visibleConnectionFieldKeys: FieldKey[]) =>
	createSelector(
		getSelectedIssueLocalIssueId,
		getConnectionProperties,
		getIssuesWithBasicPropertiesMap,
		getConnectedIssuesWithBasicPropertiesMap,
		getConnectionIssueIds,
		getJiraIdToLocalIssueId,
		getSelectedIssueConfiguredConnectionFields,
		(
			localIssueId,
			connectionProperties,
			issuesWithBasicPropertiesMap,
			connectedIssuesWithBasicPropertiesMap,
			connectionIssueIds,
			jiraIdToLocalIssueId,
			fields,
		) => {
			if (!localIssueId) {
				return 0;
			}

			return fields
				.filter(
					({ type, key }) =>
						visibleConnectionFieldKeys.includes(key) && type === FIELD_TYPES.CONNECTION,
				)
				.reduce<ConnectionFieldValue[]>((acc, field) => {
					const connections = connectionProperties[field.key]?.[localIssueId];

					if (!connections) {
						return acc;
					}

					const filteredConnections = filterArchivedConnectionFieldIssueIds(
						connections,
						issuesWithBasicPropertiesMap,
						connectedIssuesWithBasicPropertiesMap,
						connectionIssueIds,
						jiraIdToLocalIssueId,
					);

					return uniqBy([...acc, ...filteredConnections], 'id');
				}, []).length;
		},
	);

const getConnectionFields = (_state: State, props: Props) =>
	props.fields?.filter(({ type }) => type === FIELD_TYPES.CONNECTION);

const isConnectionFieldThatNeedsDeliveryData = ({
	connectionField,
	allFieldsByKey,
}: {
	connectionField: Field;
	allFieldsByKey: Record<FieldKey, Field>;
}) => {
	const highlightedFieldKey = connectionField.configuration?.issueViewLayout?.fields?.[0];
	if (!highlightedFieldKey) {
		return false;
	}

	const highlightedField = allFieldsByKey[highlightedFieldKey];
	return (
		highlightedField?.type === FIELD_TYPES.DELIVERY_PROGRESS ||
		highlightedField?.type === FIELD_TYPES.DELIVERY_STATUS ||
		(highlightedField?.type === FIELD_TYPES.INTERVAL &&
			highlightedField?.configuration?.source === INTERVAL_FIELD_SOURCES.DELIVERY_DATE)
	);
};

export const createGetIsConnectionFieldThatNeedsDeliveryData = (fieldKey?: FieldKey) =>
	createSelector(getAllFieldsByKey, (allFieldsByKey) => {
		if (!fieldKey) {
			return false;
		}

		const connectionField = allFieldsByKey[fieldKey];
		if (!connectionField || connectionField.type !== FIELD_TYPES.CONNECTION) {
			return false;
		}

		return isConnectionFieldThatNeedsDeliveryData({
			connectionField,
			allFieldsByKey,
		});
	});

export const getConnectionFieldsThatNeedDeliveryData = createSelector(
	getConnectionFields,
	getAllFieldsByKey,
	(connectionFields, allFieldsByKey) => {
		return (
			connectionFields?.filter((connectionField) =>
				isConnectionFieldThatNeedsDeliveryData({ connectionField, allFieldsByKey }),
			) ?? []
		);
	},
);

export const createGetConnectionsValuesMap = (fieldKey: FieldKey | undefined) =>
	createSelector(
		getFieldMappings,
		(state: State) => state,
		(_: State, props?: Props) => props,
		(mappings, state, props): Record<string, ConnectionFieldValue[] | undefined> | undefined =>
			fieldKey ? mappings[fieldKey].getAllValues(state, props) : undefined,
	);

export const createGetConnectionsValueSelector = (
	fieldKey: FieldKey | undefined,
	id: LocalIssueId,
) =>
	createSelector(
		getFieldMappings,
		(state: State) => state,
		(_: State, props?: Props) => props,
		(mappings, state, props): ConnectionFieldValue[] | undefined =>
			fieldKey ? mappings[fieldKey].valueAccessor(state, props, id) : undefined,
	);
